import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, CircularProgress, Chip, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, alpha, Avatar, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import CategoryIcon from "@mui/icons-material/Category";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SpeedIcon from "@mui/icons-material/Speed";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PageLoader from "../../Components/Loader/PageLoader";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface DashboardData {
  products: { total: number; active: number; low_stock: number; out_of_stock: number };
  shops: { total: number };
  suppliers: { total: number };
  categories: { total: number };
  sales: { today_count: number; today_total: number; month_count: number; month_total: number };
  orders: { total: number; pending: number };
  invoices: { total: number; unpaid: number };
  credit: { total_outstanding: number; shops_near_limit: number };
  recent_products: any[];
  top_products: any[];
  low_stock_list: any[];
}

// ─── Premium Card (clean white, subtle depth) ───────────────────────────────
const GlassCard = ({ children, sx, onClick }: { children: React.ReactNode; sx?: any; onClick?: () => void }) => (
  <Box onClick={onClick} sx={{
    borderRadius: "16px", p: 3,
    background: "#FFFFFF",
    border: "1px solid #EEF2F7",
    boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
    transition: "all 0.2s ease",
    cursor: onClick ? "pointer" : "default",
    "&:hover": onClick ? {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
      borderColor: "#E2E8F0",
    } : {},
    ...sx,
  }}>{children}</Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening");
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("token") || "";
    fetch(`${API_URL}/dashboard/summary`, {
      headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
    })
      .then(r => r.json())
      .then(json => { if (json.status && json.data) setData(json.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  if (!data) return (
    <Box sx={{ textAlign: "center", py: 12, background: "linear-gradient(135deg, #f0f9ff 0%, #faf5ff 50%, #fff1f2 100%)", minHeight: "80vh" }}>
      <Typography fontSize={18} fontWeight={600} color="#374151">Unable to load dashboard</Typography>
      <Typography fontSize={13} color="#94a3b8" mt={1}>Please check your connection and try again</Typography>
    </Box>
  );

  const d = data;
  const stockHealth = d.products.total > 0 ? Math.round(((d.products.total - d.products.low_stock - d.products.out_of_stock) / d.products.total) * 100) : 100;
  const today = new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <Box sx={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
      p: { xs: 2, md: 3 },
    }}>

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO HEADER
      ═══════════════════════════════════════════════════════════════════════ */}
      <Box sx={{
        borderRadius: "16px", p: { xs: 3, md: 4.5 }, mb: 4,
        background: "linear-gradient(135deg, #0B1E2D 0%, #12324A 50%, #1F6F78 100%)",
        position: "relative", overflow: "hidden",
        boxShadow: "0 12px 32px rgba(11,30,45,0.25)",
        "&::after": {
          content: '""',
          position: "absolute",
          width: "420px",
          height: "420px",
          background: "radial-gradient(circle, rgba(14,165,233,0.18), transparent 70%)",
          top: "-140px",
          right: "-120px",
          pointerEvents: "none",
        },
      }}>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }} />
                <Typography fontSize={12} color="rgba(255,255,255,0.6)" fontWeight={500}>{today}</Typography>
              </Box>
              <Typography fontSize={{ xs: 26, md: 32 }} fontWeight={800} color="#FFFFFF" letterSpacing="-0.02em" lineHeight={1.2}>
                {greeting} 👋
              </Typography>
              <Typography fontSize={14} color="rgba(255,255,255,0.75)" mt={0.75}>
                Here's your business at a glance
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: { xs: 1, md: 0 } }}>
              <Button size="small" variant="contained" disableElevation
                startIcon={<PointOfSaleIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/sales")}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2.5, py: 0.85,
                  background: "linear-gradient(135deg, #1F6F78 0%, #0EA5E9 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0 6px 18px rgba(14,165,233,0.30)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1A5C63 0%, #0284C7 100%)",
                    boxShadow: "0 8px 22px rgba(14,165,233,0.40)",
                    transform: "translateY(-1px)",
                  },
                }}>
                Open POS
              </Button>
              <Button size="small" variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/product/create")}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, py: 0.85,
                  borderColor: "rgba(255,255,255,0.35)", color: "#FFFFFF",
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(10px)",
                  "&:hover": { borderColor: "rgba(255,255,255,0.6)", bgcolor: "rgba(255,255,255,0.12)" },
                }}>
                New Product
              </Button>
            </Box>
          </Box>

          {/* Hero KPI Strip */}
          <Grid container spacing={2} sx={{ mt: 4 }}>
            {[
              { label: "Today's Revenue", value: `$${d.sales.today_total.toFixed(2)}`, sub: `${d.sales.today_count} sale${d.sales.today_count !== 1 ? "s" : ""}`, icon: <AttachMoneyIcon />, gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)", glow: "rgba(16,185,129,0.35)" },
              { label: "Monthly Revenue", value: `$${d.sales.month_total.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`, sub: `${d.sales.month_count} transactions`, icon: <TrendingUpIcon />, gradient: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)", glow: "rgba(14,165,233,0.35)" },
              { label: "Outstanding Credit", value: `$${d.credit.total_outstanding.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`, sub: d.credit.shops_near_limit > 0 ? `${d.credit.shops_near_limit} near limit` : "All healthy", icon: <AccountBalanceWalletIcon />, gradient: d.credit.shops_near_limit > 0 ? "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" : "linear-gradient(135deg, #64748B 0%, #475569 100%)", glow: d.credit.shops_near_limit > 0 ? "rgba(249,115,22,0.35)" : "rgba(100,116,139,0.25)" },
              { label: "Total Products", value: d.products.total, sub: `${d.products.active} active`, icon: <InventoryIcon />, gradient: "linear-gradient(135deg, #1F6F78 0%, #0EA5E9 100%)", glow: "rgba(31,111,120,0.35)" },
            ].map((kpi, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box sx={{
                  p: 2.5, borderRadius: "16px",
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: "rgba(255,255,255,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
                      background: kpi.gradient,
                      boxShadow: `0 4px 14px ${kpi.glow}`,
                    }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 17, color: "#FFFFFF" } })}
                    </Box>
                    <Typography fontSize={11} fontWeight={600} color="rgba(255,255,255,0.6)" textTransform="uppercase" letterSpacing="0.06em">
                      {kpi.label}
                    </Typography>
                  </Box>
                  <Typography fontSize={22} fontWeight={800} color="#FFFFFF" sx={{ fontVariantNumeric: "tabular-nums" }}>{kpi.value}</Typography>
                  <Typography fontSize={12} color="rgba(255,255,255,0.6)" mt={0.5}>{kpi.sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT
      ═══════════════════════════════════════════════════════════════════════ */}
      <Grid container spacing={3}>

        {/* ═══ LEFT COLUMN ═══ */}
        <Grid item xs={12} lg={8}>

          {/* Quick Nav Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { icon: <StorefrontIcon />, label: "Restaurants", value: d.shops.total, grad: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)", glow: "rgba(139,92,246,0.30)", path: "/shop/list" },
              { icon: <LocalShippingIcon />, label: "Suppliers", value: d.suppliers.total, grad: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)", glow: "rgba(249,115,22,0.30)", path: "/vendor/list" },
              { icon: <ShoppingCartIcon />, label: "Orders", value: d.orders.total, grad: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)", glow: "rgba(14,165,233,0.30)", path: "/shop-order/list" },
              { icon: <ReceiptIcon />, label: "Invoices", value: d.invoices.total, grad: "linear-gradient(135deg, #10B981 0%, #059669 100%)", glow: "rgba(16,185,129,0.30)", path: "/shop-invoice/list" },
            ].map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <GlassCard onClick={() => navigate(s.path)} sx={{ p: 2.5 }}>
                  <Box sx={{
                    width: 42, height: 42, borderRadius: "10px", mb: 2,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: s.grad,
                    boxShadow: `0 6px 18px ${s.glow}`,
                  }}>
                    {React.cloneElement(s.icon, { sx: { fontSize: 20, color: "#FFFFFF" } })}
                  </Box>
                  <Typography fontSize={24} fontWeight={800} color="#0F172A" lineHeight={1.1}>{s.value}</Typography>
                  <Typography fontSize={12} fontWeight={600} color="#6B7280" mt={0.5} textTransform="uppercase" letterSpacing="0.04em">{s.label}</Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>

          {/* Stock Alerts */}
          {(d.products.low_stock > 0 || d.products.out_of_stock > 0) && (
            <GlassCard sx={{ mb: 3, p: 0, overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #EEF2F7" }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
                  boxShadow: "0 4px 12px rgba(249,115,22,0.30)",
                }}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
                </Box>
                <Typography fontSize={14} fontWeight={700} color="#0F172A">Stock Alerts</Typography>
                <Chip label={d.products.low_stock + d.products.out_of_stock} size="small"
                  sx={{ ml: "auto", height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#FFF7ED", color: "#EA580C", border: "1px solid #FED7AA" }} />
              </Box>
              <Box sx={{ p: 3 }}>
                {d.products.out_of_stock > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, mb: d.products.low_stock > 0 ? 1 : 0, borderRadius: "10px", bgcolor: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}>
                    <ErrorOutlineIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={13} fontWeight={700} color="#DC2626">{d.products.out_of_stock} product{d.products.out_of_stock !== 1 ? "s" : ""} out of stock</Typography>
                      <Typography fontSize={12} color="#F87171" mt={0.25}>Immediate restocking needed</Typography>
                    </Box>
                  </Box>
                )}
                {d.products.low_stock > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2, borderRadius: "10px", bgcolor: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.18)" }}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: "#F97316" }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={13} fontWeight={700} color="#C2410C">{d.products.low_stock} product{d.products.low_stock !== 1 ? "s" : ""} below reorder point</Typography>
                      <Typography fontSize={12} color="#EA580C" mt={0.25}>Consider placing purchase orders</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </GlassCard>
          )}

          {/* Low Stock Table */}
          {d.low_stock_list.length > 0 && (
            <GlassCard sx={{ mb: 3, p: 0, overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EEF2F7" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
                    background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)",
                    boxShadow: "0 4px 12px rgba(249,115,22,0.30)",
                  }}>
                    <InventoryIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
                  </Box>
                  <Typography fontSize={15} fontWeight={700} color="#0F172A">Low Stock Products</Typography>
                </Box>
                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate("/product/list")}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: "#0EA5E9", borderRadius: "8px", "&:hover": { bgcolor: "rgba(14,165,233,0.08)" } }}>View All</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Product", "Stock", "Reorder At", "Status"].map(h => (
                        <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280", py: 1.5, bgcolor: "#F8FAFC", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #EEF2F7" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.low_stock_list.map((p: any) => {
                      const isOut = Number(p.current_stock) <= 0;
                      const pct = Number(p.reorder_point) > 0 ? Math.min(100, (Number(p.current_stock) / Number(p.reorder_point)) * 100) : 0;
                      return (
                        <TableRow key={p.id} hover sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F9FAFB" } }} onClick={() => navigate(`/product-history/${p.id}`)}>
                          <TableCell sx={{ fontSize: 13, fontWeight: 500, color: "#111827", py: 1.5 }}>{p.productName}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography fontSize={13} fontWeight={700} color={isOut ? "#DC2626" : "#F97316"}>{Number(p.current_stock).toFixed(0)}</Typography>
                              <LinearProgress variant="determinate" value={pct}
                                sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: "#F1F5F9", maxWidth: 60,
                                  "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: isOut ? "#DC2626" : "#F97316" } }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: "#6B7280", py: 1.5 }}>{Number(p.reorder_point).toFixed(0)}</TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip label={isOut ? "Out of Stock" : "Low Stock"} size="small"
                              sx={{ height: 22, fontSize: 10, fontWeight: 700, borderRadius: "6px",
                                bgcolor: isOut ? "#FEF2F2" : "#FFF7ED", color: isOut ? "#DC2626" : "#F97316",
                                border: `1px solid ${isOut ? "#FECACA" : "#FED7AA"}` }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </GlassCard>
          )}

          {/* Recent Products */}
          <GlassCard sx={{ p: 0, overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EEF2F7" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)",
                  boxShadow: "0 4px 12px rgba(14,165,233,0.30)",
                }}>
                  <AddIcon sx={{ fontSize: 15, color: "#FFFFFF" }} />
                </Box>
                <Typography fontSize={15} fontWeight={700} color="#0F172A">Recently Added</Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate("/product/list")}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: "#0EA5E9", borderRadius: "8px", "&:hover": { bgcolor: "rgba(14,165,233,0.08)" } }}>View All</Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Product", "Price", "Added"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280", py: 1.5, bgcolor: "#F8FAFC", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #EEF2F7" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.recent_products.map((p: any) => (
                    <TableRow key={p.id} hover sx={{ cursor: "pointer", "&:hover": { bgcolor: "#F9FAFB" } }} onClick={() => navigate(`/product-history/${p.id}`)}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 500, color: "#111827", py: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "#F8FAFC", fontSize: 12, fontWeight: 600, color: "#6B7280", border: "1px solid #EEF2F7" }}>
                            {p.productName?.[0]?.toUpperCase() || "P"}
                          </Avatar>
                          {p.productName}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#059669", py: 1.5 }}>${Number(p.salePrice || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#6B7280", py: 1.5 }}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </GlassCard>
        </Grid>

        {/* ═══ RIGHT COLUMN ═══ */}
        <Grid item xs={12} lg={4}>

          {/* Quick Actions */}
          <GlassCard sx={{ mb: 3 }}>
            <Typography fontSize={14} fontWeight={700} color="#0F172A" mb={2}>Quick Actions</Typography>
            <Grid container spacing={1.5}>
              {[
                { label: "Open POS", icon: <PointOfSaleIcon />, grad: "linear-gradient(135deg, #1F6F78 0%, #0EA5E9 100%)", glow: "rgba(14,165,233,0.30)", path: "/sales" },
                { label: "New Product", icon: <AddIcon />, grad: "linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)", glow: "rgba(14,165,233,0.30)", path: "/product/create" },
                { label: "Add Restaurant", icon: <StorefrontIcon />, grad: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)", glow: "rgba(139,92,246,0.30)", path: "/shop/create" },
                { label: "Add Supplier", icon: <LocalShippingIcon />, grad: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)", glow: "rgba(249,115,22,0.30)", path: "/vendor/create" },
              ].map((a, i) => (
                <Grid item xs={6} key={i}>
                  <Button fullWidth size="small" disableElevation
                    startIcon={React.cloneElement(a.icon, { sx: { fontSize: 16 } })}
                    onClick={() => navigate(a.path)}
                    sx={{
                      borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 700, py: 1.4,
                      background: a.grad, color: "#FFFFFF",
                      boxShadow: `0 4px 14px ${a.glow}`,
                      transition: "all 0.2s ease",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 22px ${a.glow}` },
                    }}>
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </GlassCard>

          {/* Inventory Health */}
          <GlassCard sx={{ mb: 3 }}>
            <Typography fontSize={14} fontWeight={700} color="#0F172A" mb={2.5}>Inventory Health</Typography>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress variant="determinate" value={100} size={120} thickness={4}
                  sx={{ color: "#F1F5F9", position: "absolute" }} />
                <CircularProgress variant="determinate" value={stockHealth} size={120} thickness={4}
                  sx={{
                    color: stockHealth > 80 ? "#10B981" : stockHealth > 50 ? "#F97316" : "#EF4444",
                    "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                    filter: `drop-shadow(0 2px 8px ${alpha(stockHealth > 80 ? "#10B981" : stockHealth > 50 ? "#F97316" : "#EF4444", 0.3)})`,
                  }} />
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                  <Typography fontSize={28} fontWeight={800} color="#0F172A" lineHeight={1}>{stockHealth}%</Typography>
                  <Typography fontSize={10} fontWeight={700} color="#94A3B8" textTransform="uppercase" letterSpacing="0.08em" mt={0.5}>Healthy</Typography>
                </Box>
              </Box>
            </Box>
            {[
              { label: "Total", value: d.products.total, color: "#0EA5E9" },
              { label: "Active", value: d.products.active, color: "#10B981" },
              { label: "Low Stock", value: d.products.low_stock, color: "#F97316" },
              { label: "Out of Stock", value: d.products.out_of_stock, color: "#EF4444" },
            ].map((item, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.25, borderBottom: i < 3 ? "1px solid #F1F5F9" : "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 9, color: item.color }} />
                  <Typography fontSize={13} color="#64748B" fontWeight={500}>{item.label}</Typography>
                </Box>
                <Typography fontSize={13} fontWeight={700} color={item.value > 0 && (item.label === "Low Stock" || item.label === "Out of Stock") ? item.color : "#0F172A"}>{item.value}</Typography>
              </Box>
            ))}
          </GlassCard>

          {/* Business Summary */}
          <GlassCard>
            <Typography fontSize={14} fontWeight={700} color="#0F172A" mb={2}>Business Summary</Typography>
            {[
              { label: "Categories", value: d.categories.total, icon: <CategoryIcon />, color: "#10B981" },
              { label: "Pending Orders", value: d.orders.pending, icon: <ShoppingCartIcon />, color: "#0EA5E9" },
              { label: "Unpaid Invoices", value: d.invoices.unpaid, icon: <ReceiptIcon />, color: "#F97316" },
              { label: "Shops Near Limit", value: d.credit.shops_near_limit, icon: <WarningAmberIcon />, color: "#EF4444" },
            ].map((item, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                py: 1.25, borderBottom: i < 3 ? "1px solid #F1F5F9" : "none",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(item.color, 0.10) }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 15, color: item.color } })}
                  </Box>
                  <Typography fontSize={13} color="#64748B" fontWeight={500}>{item.label}</Typography>
                </Box>
                <Chip label={item.value} size="small"
                  sx={{
                    height: 24, minWidth: 32, fontSize: 11, fontWeight: 800, borderRadius: "8px",
                    bgcolor: item.value > 0 ? alpha(item.color, 0.10) : "#F1F5F9",
                    color: item.value > 0 ? item.color : "#94A3B8",
                    border: `1px solid ${item.value > 0 ? alpha(item.color, 0.20) : "transparent"}`,
                  }} />
              </Box>
            ))}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
